/**
 * Générateur — Attendu 197 — O7 « Il décompose un problème en sous-problèmes et
 * traduit un sous-problème en créant un "bloc-personnalisé". »
 * Niveau : 4ème | Thème #12 Algorithmique (O7, Niveau 3) | Session B.
 * Source prof : O7 est MUET en texte ; critère canonique = les « Exemples de
 * réussite » de l'image page 2 (bloc personnalisé « Motif », frise reproduite).
 * → BANQUE construite sur ce critère.
 *
 * ADAPTATIONS scoring : « crée un bloc » n'est pas éditable → méthode de
 * décomposition en ORDONNER (chaîne unique) ; « quels sous-problèmes » → QCM
 * multiple concret ; « combien d'appels » → saisie calculée.
 *
 * 4 cartes :
 *  1. methode   — ordonner les étapes de la décomposition (ordonner).
 *  2. decompo   — quels sous-problèmes définir pour une figure composée (QCM multiple).
 *  3. appels    — un bloc appelé n fois : combien de côtés au total ? (saisie).
 *  4. avantage  — pourquoi créer un bloc personnalisé ? (QCM).
 */

import {
    randInt, randomChoice, shuffle, makeQcm, makeSaisie, makeOrdonner,
} from '../../../utils';

const ATTENDU_ID = 197;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_METHODE = 'Décomposer : les étapes dans l\'ordre';
const L_DECOMPO = 'Décomposer : quels sous-problèmes ?';
const L_APPELS = 'Le bloc personnalisé : combien d\'appels ?';
const L_AVANTAGE = 'Pourquoi un bloc personnalisé ?';

// ═════════ Carte 1 — méthode de décomposition (ordonner) ═════════

const FIGURES = ['frise', 'rosace', 'bordure', 'guirlande', 'mosaïque'];
const MOTIFS_M = ['étoile', 'vague', 'losange', 'fleur', 'flèche'];

const genMethode = () => {
    const figure = randomChoice(FIGURES);
    const motif = randomChoice(MOTIFS_M);
    const items = [
        `Découper la ${figure} en sous-figures simples (le motif « ${motif} »).`,
        `Définir un bloc personnalisé qui trace « ${motif} ».`,
        'Tester le bloc sur un seul motif.',
        `Appeler le bloc dans une boucle pour reproduire toute la ${figure}.`,
    ];
    const texte = `<p><b>Pour dessiner une ${figure} compliquée, on la décompose en sous-problèmes. `
        + 'Remets les étapes de la méthode dans l\'ordre.</b></p>';
    const payload = makeOrdonner(items, texte, opts(L_METHODE));
    payload._meta.params = { figure, motif };
    return payload;
};

// ═════════ Carte 2 — quels sous-problèmes ? (QCM multiple) ═════════

const OBJETS = [
    { nom: 'une maison', parts: ['le toit', 'les murs', 'la porte'] },
    { nom: 'un robot', parts: ['la tête', 'le corps', 'les bras'] },
    { nom: 'une voiture', parts: ['la carrosserie', 'les roues', 'les phares'] },
    { nom: 'une fleur', parts: ['la tige', 'les pétales', 'le cœur'] },
    { nom: 'un arbre', parts: ['le tronc', 'les branches', 'les feuilles'] },
    { nom: 'un bateau', parts: ['la coque', 'la voile', 'le mât'] },
    { nom: 'une fusée', parts: ['le corps', 'les ailerons', 'le hublot'] },
    { nom: 'un chat', parts: ['la tête', 'le corps', 'la queue'] },
];

// intrus = des ACTIONS (commencent par un verbe), jamais des sous-figures
const INTRUS_POOL = [
    'effacer tout', 'attendre 1 seconde', 'changer de costume', 'jouer un son',
    'dire bonjour', 'cacher le lutin', 'changer de fond', 'attendre 2 secondes',
];

const genDecompo = () => {
    const o = randomChoice(OBJETS);
    const intrus = shuffle([...INTRUS_POOL]).slice(0, 2);
    const choix = shuffle([...o.parts, ...intrus]);
    const texte = `<p><b>Pour dessiner ${o.nom} avec des blocs personnalisés, quels sous-problèmes `
        + '(sous-figures) est-il utile de définir ? (Coche tous les bons.)</b></p>';
    const payload = makeQcm(choix, o.parts, texte, opts(L_DECOMPO), true);
    payload._meta.params = { nom: o.nom };
    return payload;
};

// ═════════ Carte 3 — combien d'appels ? (saisie) ═════════

const POLYS = [
    { nom: 'un triangle', s: 3 },
    { nom: 'un carré', s: 4 },
    { nom: 'un pentagone', s: 5 },
    { nom: 'un hexagone', s: 6 },
];

const genAppels = () => {
    const p = randomChoice(POLYS);
    const n = randInt(2, 8);
    const pas = randInt(2, 12) * 10;
    const total = n * p.s;
    const texte = `<p><b>Un bloc personnalisé « Forme » trace ${p.nom} (${p.s} côtés). `
        + `Le programme principal exécute « répéter ${n} fois [ Forme, avancer de ${pas} pas ] ».</b></p>`
        + '<p>Combien de côtés le lutin trace-t-il en tout ?</p>';
    const payload = makeSaisie(String(total), texte, opts(L_APPELS));
    payload._meta.params = { s: p.s, n, total };
    return payload;
};

// ═════════ Carte 4 — pourquoi un bloc personnalisé ? (QCM) ═════════

const MOTIFS = ['une étoile', 'une vague', 'un carré', 'une spirale', 'une brique'];

const genAvantage = () => {
    const motif = randomChoice(MOTIFS);
    const n = randInt(4, 12);
    const correct = 'Pour réutiliser le motif plusieurs fois sans recopier les mêmes blocs.';
    const choix = [
        correct,
        'Parce que le programme s\'exécute alors beaucoup plus vite.',
        'Parce qu\'un programme ne peut pas contenir plus de 10 blocs.',
        'Pour empêcher les autres lutins de bouger.',
    ];
    const texte = `<p><b>Le motif « ${motif} » est utilisé ${n} fois dans une frise. `
        + 'Pourquoi le définir comme bloc personnalisé plutôt que recopier ses blocs '
        + `${n} fois ?</b></p>`;
    const payload = makeQcm(choix, correct, texte, opts(L_AVANTAGE));
    payload._meta.params = { motif, n };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    methode_ordonner: genMethode,
    decompo_qcm_multiple: genDecompo,
    appels_texte_saisie: genAppels,
    avantage_qcm_unique: genAvantage,
};

const CARTES = [
    { id: 'methode', label: L_METHODE },
    { id: 'decompo', label: L_DECOMPO },
    { id: 'appels', label: L_APPELS },
    { id: 'avantage', label: L_AVANTAGE },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
